import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
  isAdmin?: boolean;
}

export const Layout = ({ children, isAdmin = false }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (isAdmin) {
    return (
      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <div className="sidebar-content">
            <div className="sidebar-header">
              <div className="profile-avatar">
                <span className="material-symbols-outlined">admin_panel_settings</span>
              </div>
              <div className="profile-info">
                <h1>EK Library</h1>
                <p>Admin Panel</p>
              </div>
            </div>

            <nav className="sidebar-nav">
              <Link
                to="/admin/dashboard"
                className={`nav-item ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}
              >
                <span className="material-symbols-outlined">dashboard</span>
                <span>Dashboard</span>
              </Link>
              <Link
                to="/admin/books"
                className={`nav-item ${location.pathname === '/admin/books' ? 'active' : ''}`}
              >
                <span className="material-symbols-outlined">library_books</span>
                <span>Books</span>
              </Link>
              <Link
                to="/admin/shelves"
                className={`nav-item ${location.pathname === '/admin/shelves' ? 'active' : ''}`}
              >
                <span className="material-symbols-outlined">shelves</span>
                <span>Shelves</span>
              </Link>
              <Link
                to="/admin/assignees"
                className={`nav-item ${location.pathname === '/admin/assignees' ? 'active' : ''}`}
              >
                <span className="material-symbols-outlined">group_add</span>
                <span>Assignees</span>
              </Link>
            </nav>

            <div className="sidebar-footer">
              <button onClick={handleLogout} className="nav-item logout-btn">
                <span className="material-symbols-outlined">logout</span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          <header className="admin-header">
            <div className="breadcrumb">
              <span>Admin</span>
              <span className="material-symbols-outlined">chevron_right</span>
              <span className="current">
                {location.pathname === '/admin/dashboard' && 'Dashboard Overview'}
                {location.pathname === '/admin/books' && 'Books Management'}
                {location.pathname === '/admin/shelves' && 'Shelf Management'}
                {location.pathname === '/admin/assignees' && 'Assignees Management'}
              </span>
            </div>
            <div className="header-actions">
              <Link to="/" className="btn btn-primary home-btn">
                <span>Home</span>
              </Link>
              <button
                className="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <span className="material-symbols-outlined">menu</span>
              </button>
            </div>
          </header>

          <div className="admin-content">
            {children}
          </div>
        </main>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div
            className="mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </div>
    );
  }

  // Public layout
  return (
    <div className="public-layout">
      <header className="public-header">
        <div className="header-left">
          <div className="logo">
            <span className="material-symbols-outlined">local_library</span>
          </div>
          <h2>EK Library</h2>
        </div>
        <div className="header-right">
          <Link to="/admin/login" className="btn btn-primary login-btn">
            <span>Admin Login</span>
          </Link>
        </div>
      </header>

      <main className="public-main">
        {children}
      </main>

      <footer className="public-footer">
        <p>© {new Date().getFullYear()} EK Library System. All rights reserved.</p>
      </footer>
    </div>
  );
};
